import {
  logEvent as _logEvent,
  setUserProperties as _setUserProperties,
  getAnalytics
} from "firebase/analytics";
import { initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { getDatabase } from "firebase/database";
import { initializeFirestore } from "firebase/firestore";
import { getFunctions } from "firebase/functions";
import { getMessaging, getToken, isSupported } from "firebase/messaging";
import { getStorage } from "firebase/storage";

const IS_GTM_MODE =
  process.env.NODE_ENV === "production" && Boolean(process.env.NEXT_PUBLIC_GTM_ID);

const shouldUseFetchStreams = () => {
  if (typeof navigator === "undefined") return false;

  const userAgent = navigator.userAgent;
  const isIOS =
    /iPad|iPhone|iPod/.test(userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isMacSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(userAgent);

  return !(isIOS || isMacSafari);
};

const pushDataLayerEvent = (eventName: string, eventParams?: any) => {
  if (typeof window === "undefined") return;

  const win = window as any;
  win.dataLayer = win.dataLayer || [];
  win.dataLayer.push({ event: eventName, ...(eventParams ?? {}) });
};

export const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTHDOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
  databaseURL: process.env.NEXT_PUBLIC_DATABASE_URL
};

// Initialize Firebase
export const app = initializeApp(firebaseConfig);

export const db = initializeFirestore(app, {
  useFetchStreams: shouldUseFetchStreams()
} as any);
export const functions = getFunctions(app);
export const storage = getStorage();
export const auth = getAuth(app);
export const realtimeDB = getDatabase(app);

export const analytics =
  typeof window !== "undefined" && !IS_GTM_MODE ? getAnalytics(app) : null;

// Initialize FCM messaging
export const getMessagingInstance = async () => {
  if (typeof window === "undefined") return null;

  const supported = await isSupported();
  return supported ? getMessaging(app) : null;
};

// For backward compatibility - will be null initially
export let messaging: any = null;

// Initialize messaging when supported
if (typeof window !== "undefined") {
  getMessagingInstance().then((instance) => {
    messaging = instance;
  });
}

export const fetchToken = async () => {
  try {
    const fcmMessaging = await getMessagingInstance();
    if (fcmMessaging) {
      const token = await getToken(fcmMessaging, {
        vapidKey: process.env.NEXT_PUBLIC_FIREBASE_FCM_VAPID_KEY
      });
      return token;
    }
    return null;
  } catch (err) {
    console.error("An error occurred while fetching the token:", err);
    return null;
  }
};

export const logEvent = (eventName: string, eventParams?: any) => {
  if (IS_GTM_MODE) {
    pushDataLayerEvent(eventName, eventParams);
    return;
  }

  if (analytics) {
    _logEvent(analytics, eventName, eventParams);
  }
};

export const setUserProperties = (properties: Record<string, string | undefined>) => {
  const userProps = Object.entries(properties).reduce((acc, [key, value]) => {
    if (value !== undefined) acc[key] = value;
    return acc;
  }, {} as Record<string, string>);

  if (IS_GTM_MODE) {
    pushDataLayerEvent("set_user_properties", { user_properties: userProps });
    return;
  }

  if (analytics) {
    _setUserProperties(analytics, userProps);
  }
};

export const setUserProperty = (name: string, value: string) => {
  setUserProperties({ [name]: value });
};

// TODO: localでfunctionsを確認したい時はここのコメントアウトを外すが、デプロイする際には必ずコメントアウトすること。そうでないと本番環境でもlocalhostでfetchしてエラーになる。
// const devFunctions = getFunctions(getApp());
// functionsのエミュレータのURLによってどちらかを使う
// ref:https://firebase.google.com/docs/emulator-suite/connect_functions?hl=ja#instrument_your_app_to_talk_to_the_emulators
// connectFunctionsEmulator(functions, "127.0.0.1", 5001);
// connectFunctionsEmulator(devFunctions, "localhost", 5001);
